import { generateDatadogTrackingProps } from 'toolkit/environment/environment/userMonitoring.util';
import type { ReportMeasurementPeriodOptionDto } from '@act/shared/data-transfer-objects';
import { MenuItem, Select, Typography } from '@mui/material';
import ReportFormField from './ReportFormField';
import type { MeasurementPeriodMatchStatus } from '../utils/reportMetadataFormOptions.util';
import { buildMeasurementPeriodMatchHelperLines } from '../utils/reportMetadataFormOptions.util';

interface ReportMeasurementPeriodSelectProps {
  label: string;
  value: string;
  options: ReportMeasurementPeriodOptionDto[];
  loading: boolean;
  required?: boolean;
  matchStatus?: MeasurementPeriodMatchStatus;
  autoMatchedLabel?: string;
  onChange: (value: string) => void;
}

/**
 * Measurement period dropdown shared by JOC and monthly/quarterly report forms.
 * @param props - Select state, options, and optional auto-match helper copy.
 * @returns Measurement period form field.
 */
const ReportMeasurementPeriodSelect = ({
  label,
  value,
  options,
  loading,
  required = true,
  matchStatus,
  autoMatchedLabel = '',
  onChange,
}: ReportMeasurementPeriodSelectProps) => {
  const helperLines =
    matchStatus !== undefined
      ? buildMeasurementPeriodMatchHelperLines(matchStatus, autoMatchedLabel)
      : [];

  return (
    <ReportFormField label={label}>
      <Select
        required={required}
        fullWidth
        displayEmpty
        disabled={loading === true}
        value={value}
        onChange={event => onChange(event.target.value)}
      >
        <MenuItem {...generateDatadogTrackingProps('report-measurement-period-select-menu-item-1')} value="" disabled>
          {loading === true
            ? 'Loading measurement periods…'
            : 'Select measurement period'}
        </MenuItem>
        {options.map(option => (
          <MenuItem {...generateDatadogTrackingProps('report-measurement-period-select-menu-item-2')} key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
      {helperLines.map(line => (
        <Typography
          key={line}
          variant="caption"
          color="text.secondary"
          component="p"
        >
          {line}
        </Typography>
      ))}
      {loading !== true && options.length === 0 ? (
        <Typography variant="caption" color="text.secondary" component="p">
          No measurement periods found in the warehouse for this customer.
        </Typography>
      ) : null}
    </ReportFormField>
  );
};

export default ReportMeasurementPeriodSelect;
